// frontend/src/components/ProtectedRoute.jsx
import { Navigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useState, useEffect } from "react";
import api from "../api";

const ACCESS_TOKEN = "accessToken";
const REFRESH_TOKEN = "refreshToken";

// Helper to read from either localStorage or sessionStorage
const getStoredItem = (key) =>
  localStorage.getItem(key) || sessionStorage.getItem(key);

const hasAccessToken = () =>
  !!localStorage.getItem(ACCESS_TOKEN) ||
  !!sessionStorage.getItem(ACCESS_TOKEN);

const hasRefreshToken = () =>
  !!localStorage.getItem(REFRESH_TOKEN) ||
  !!sessionStorage.getItem(REFRESH_TOKEN);

const clearAuth = () => {
  localStorage.removeItem(ACCESS_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem("user");
  localStorage.removeItem("role");
  localStorage.removeItem("username");

  sessionStorage.removeItem(ACCESS_TOKEN);
  sessionStorage.removeItem(REFRESH_TOKEN);
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("role");
  sessionStorage.removeItem("username");
};

// Helper to write the access token back to the same storage
// where the refresh token is stored
const setAccessToken = (token) => {
  if (localStorage.getItem(REFRESH_TOKEN)) {
    localStorage.setItem(ACCESS_TOKEN, token);
  } else if (sessionStorage.getItem(REFRESH_TOKEN)) {
    sessionStorage.setItem(ACCESS_TOKEN, token);
  } else {
    // Fallback if somehow refresh token is missing
    localStorage.setItem(ACCESS_TOKEN, token);
  }
};

function ProtectedRoute({ children, allowedRoles }) {
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [hasRoleAccess, setHasRoleAccess] = useState(true);

  useEffect(() => {
    validateAuth().catch(() => {
      clearAuth();
      setIsAuthorized(false);
      setHasRoleAccess(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedRoles]);

  const refreshAccessToken = async () => {
    const refresh = getStoredItem(REFRESH_TOKEN);
    const userStr = getStoredItem("user");

    // If refresh exists but user info is missing, treat as logged out
    if (!refresh || !userStr) {
      clearAuth();
      setIsAuthorized(false);
      setHasRoleAccess(false);
      return;
    }

    try {
      const res = await api.post(
        "/api/auth/refresh",
        {},
        {
          headers: {
            Authorization: `Bearer ${refresh}`,
          },
        }
      );

      // backend might return access_token or access
      const newAccess = res.data.access_token || res.data.access;
      if (!newAccess) throw new Error("No access token returned on refresh");

      setAccessToken(newAccess);
      setIsAuthorized(true);
    } catch (error) {
      console.log("Refresh failed:", error);
      clearAuth();
      setIsAuthorized(false);
      setHasRoleAccess(false);
    }
  };

  const validateAuth = async () => {
    const token = getStoredItem(ACCESS_TOKEN);
    const userStr = getStoredItem("user");

    // No access token = not authorized
    if (!token) {
      // If refresh exists but user is missing/stale, wipe everything
      if (hasRefreshToken() && !userStr) clearAuth();

      setIsAuthorized(false);
      setHasRoleAccess(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;

      if (decoded.exp < now) {
        await refreshAccessToken(); // this will set authorized or clearAuth on fail
      } else {
        setIsAuthorized(true);
      }

      // Role-based access check
      if (allowedRoles && allowedRoles.length > 0) {
        if (!userStr) {
          clearAuth();
          setHasRoleAccess(false);
          return;
        }

        const user = JSON.parse(userStr);
        const canAccess = allowedRoles.includes(user.role);
        setHasRoleAccess(canAccess);
      } else {
        setHasRoleAccess(true);
      }
    } catch (err) {
      console.log("Token decode failed:", err);
      clearAuth();
      setIsAuthorized(false);
      setHasRoleAccess(false);
    }
  };

  if (isAuthorized === null) {
    return <div>Loading...</div>;
  }

  // Not logged in or token invalid
  if (!isAuthorized) {
    return <Navigate to="/login" replace />;
  }

  // Logged in but role not allowed
  if (!hasRoleAccess) {
    return <Navigate to="/" replace />;
  }

  // Everything OK
  return children;
}

export default ProtectedRoute;
